import React, { useEffect, useId, useRef, useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';

export interface MultiSelectOption {
  value: number;
  label: string;
}

/** Выпадающий мультиселект в стиле селектов на портале госзакупок. */
export default function MultiSelectDropdown({
  options,
  selected,
  onChange,
  placeholder,
  className = '',
  primaryValues,
  moreOptionsLabel,
  fewerOptionsLabel,
}: {
  options: MultiSelectOption[];
  selected: number[];
  onChange: (next: number[]) => void;
  placeholder: string;
  className?: string;
  primaryValues?: number[];
  moreOptionsLabel?: string;
  fewerOptionsLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const optionsId = useId();
  const ref = useRef<HTMLDivElement>(null);
  // A selected status from a shared URL must remain visible even when it is
  // outside the default list. Expanding the list never changes the filters.
  const secondary = (value: number) => primaryValues !== undefined && !primaryValues.includes(value) && !selected.includes(value);
  const hasSecondary = options.some((option) => secondary(option.value));
  const visibleOptions = options.filter((option) => showAll || !secondary(option.value));

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const toggle = (v: number) => {
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  };

  const selectedLabels = options.filter((o) => selected.includes(o.value)).map((o) => o.label);
  const summary =
    selectedLabels.length === 0
      ? placeholder
      : selectedLabels.length === 1
        ? selectedLabels[0]
        : `${selectedLabels[0]} +${selectedLabels.length - 1}`;

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={optionsId}
        className={`procurement-focus flex min-h-[44px] w-full items-center justify-between gap-2 rounded-lg border bg-white px-3.5 py-2.5 text-left text-sm transition-colors ${
          selected.length ? 'border-goszakup-300 text-slate-900' : 'border-slate-200 text-slate-500'
        }`}
      >
        <span className="truncate">{summary}</span>
        <span className="flex items-center gap-1 shrink-0">
          {selected.length > 0 && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onChange([]); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onChange([]); } }}
              className="w-5 h-5 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
            >
              <X className="w-3 h-3 text-gray-500" />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {open && (
        <div id={optionsId} className="absolute z-30 mt-1.5 max-h-72 w-full min-w-[260px] overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
          {visibleOptions.map((o) => {
            const isSel = selected.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => toggle(o.value)}
                aria-pressed={isSel}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors ${
                  isSel ? 'bg-goszakup-50 text-goszakup-800 font-semibold' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                  isSel ? 'bg-goszakup-600 border-goszakup-600' : 'border-gray-300'
                }`}>
                  {isSel && <Check className="w-3 h-3 text-white" />}
                </span>
                <span className="leading-tight">{o.label}</span>
              </button>
            );
          })}
          {hasSecondary && moreOptionsLabel && fewerOptionsLabel && (
            <button type="button" onClick={() => setShowAll((value) => !value)} aria-expanded={showAll}
              className="w-full border-t border-gray-100 px-3 py-2 text-left text-sm font-semibold text-goszakup-700 hover:bg-goszakup-50">
              {showAll ? fewerOptionsLabel : moreOptionsLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
